/**
 * Avisos do chat (F4), ativos em qualquer tela: contador de não lidas (menu e
 * título da janela) e aviso do computador (Notification) para mensagem direta
 * ou menção — se a pessoa permitiu, a conversa não está silenciada e ela não está
 * olhando a própria conversa.
 */
import { createEffect, createResource, createSignal, onCleanup } from 'solid-js'
import type { ChatConversa } from '@shared/types'
import { api, versaoDados } from '../../nucleo/cliente'
import { textoSimples } from './util'

const [conversaAberta, setConversaAberta] = createSignal<number | null>(null)
export { conversaAberta, setConversaAberta }
/** O Chat está na tela? (a conversa escolhida continua guardada mesmo fora do Chat) */
const [chatNaTela, setChatNaTela] = createSignal(false)
export { chatNaTela, setChatNaTela }

const [permissao, setPermissao] = createSignal<NotificationPermission | 'indisponivel'>(
  typeof Notification === 'undefined' ? 'indisponivel' : Notification.permission,
)
export { permissao }

export async function pedirPermissao(): Promise<void> {
  if (typeof Notification === 'undefined') return
  setPermissao(await Notification.requestPermission())
}

const TITULO = 'Werc Industrial'

/**
 * Liga os avisos (chamar dentro da casca, depois do login). Retorna o total de
 * não lidas (para o menu). Pertence a quem chama: ao sair, a casca é desmontada
 * e os avisos param junto (sem acumular a cada novo login).
 */
export function iniciarAvisos(meuId: () => number | null) {
  const [conversas] = createResource(
    () => (meuId() === null ? false : { v: versaoDados() }),
    () => api.chat.conversas().catch(() => [] as ChatConversa[]),
  )
  // última mensagem já vista por conversa (para avisar só do que é novo)
  let vistas: Map<number, string> | null = null
  const total = () => (conversas.latest ?? []).filter((c) => !c.silenciado).reduce((s, c) => s + c.naoLidas, 0)

  createEffect(() => {
    const n = total()
    document.title = n > 0 ? `(${n}) ${TITULO}` : TITULO
  })

  createEffect(() => {
    const lista = conversas.latest
    if (!lista) return
    const primeira = vistas === null
    const antes = vistas ?? new Map()
    vistas = new Map(lista.map((c) => [c.id, c.ultimaMensagem?.criadoEm ?? '']))
    if (primeira || permissao() !== 'granted') return
    for (const c of lista) {
      const u = c.ultimaMensagem
      if (!u || c.silenciado || c.mencoes === 0 || (antes.get(c.id) ?? '') >= u.criadoEm) continue
      if (chatNaTela() && conversaAberta() === c.id && document.visibilityState === 'visible') continue
      try {
        const n = new Notification(c.tipo === 'canal' ? `#${c.nome} — ${u.autor ?? ''}` : (u.autor ?? c.nome), {
          body: textoSimples(u.texto).slice(0, 180),
          tag: `werc-chat-${c.id}`,
          icon: '/icons/icon-192.png',
        })
        n.onclick = () => {
          window.focus()
          window.dispatchEvent(new CustomEvent('werc:abrir-conversa', { detail: c.id }))
          n.close()
        }
      } catch {
        /* navegador sem suporte a aviso fora de service worker: fica só o contador */
      }
    }
  })
  onCleanup(() => (document.title = TITULO))
  return total
}
